import { DEFAULT_CATALOGUE_DESIGN, type CatalogueDesignKey } from '@eshop/designs';

import type { CatalogueTemplate } from '../types';
import type { VisualTemplate } from './templateTypes';

export type CatalogueTemplateModule = { default: CatalogueTemplate };
export type CatalogueTemplateLoader = () => Promise<CatalogueTemplateModule>;
export type VisualTemplateFactory = () => VisualTemplate | Promise<VisualTemplate>;

type VisualTemplateEntry = {
  loader: VisualTemplateFactory;
  instance?: VisualTemplate;
  promise?: Promise<VisualTemplate | null>;
};

/**
 * Registro centralizado de templates del catálogo.
 *
 * Conserva la compatibilidad con los templates clásicos (estructura y lógica)
 * y añade la capa de templates visuales descrita en {@link VisualTemplate}.
 */
export class TemplateRegistry {
  private readonly catalogueLoaders = new Map<string, CatalogueTemplateLoader>();

  private readonly visualTemplates = new Map<CatalogueDesignKey, VisualTemplateEntry>();

  registerCatalogueLoader(name: string, loader: CatalogueTemplateLoader): void {
    this.catalogueLoaders.set(name, loader);
  }

  listCatalogueTemplates(): string[] {
    return Array.from(this.catalogueLoaders.keys());
  }

  async loadCatalogueTemplate(name: string): Promise<CatalogueTemplate> {
    const loader = this.catalogueLoaders.get(name);
    if (!loader) {
      throw new Error(`Template "${name}" no registrado`);
    }
    const mod = await loader();
    return mod.default;
  }

  registerTemplate(
    key: CatalogueDesignKey,
    templateOrFactory: VisualTemplate | VisualTemplateFactory,
  ): void {
    const loader: VisualTemplateFactory =
      typeof templateOrFactory === 'function' ? templateOrFactory : () => templateOrFactory;

    this.visualTemplates.set(key, { loader });
  }

  private async instantiateTemplate(key: CatalogueDesignKey): Promise<VisualTemplate | null> {
    const entry = this.visualTemplates.get(key);
    if (!entry) {
      return null;
    }

    if (entry.instance) {
      return entry.instance;
    }

    if (entry.promise) {
      return entry.promise;
    }

    const loadPromise: Promise<VisualTemplate | null> = Promise.resolve(entry.loader())
      .then((instance) => {
        const resolved = instance ?? null;
        this.visualTemplates.set(key, { ...entry, instance: resolved ?? undefined, promise: undefined });
        return resolved;
      })
      .catch((error) => {
        this.visualTemplates.set(key, { ...entry, promise: undefined });
        throw error;
      });

    this.visualTemplates.set(key, { ...entry, promise: loadPromise });
    return loadPromise;
  }

  async resolveTemplate(
    designKey: CatalogueDesignKey,
    fallbackKey: CatalogueDesignKey = DEFAULT_CATALOGUE_DESIGN,
  ): Promise<VisualTemplate> {
    const resolved = await this.instantiateTemplate(designKey);
    if (resolved) {
      return resolved;
    }

    if (designKey !== fallbackKey) {
      const fallback = await this.instantiateTemplate(fallbackKey);
      if (fallback) {
        return fallback;
      }
    }

    // Cuando no hay coincidencias se debe recurrir al template por defecto
    // configurado en SiteSettings (`DEFAULT_CATALOGUE_DESIGN`). Lanzamos un
    // error explícito para detectar configuraciones inválidas en entornos de
    // desarrollo.
    throw new Error(
      `VisualTemplate "${designKey}" no registrado y no existe fallback "${fallbackKey}"`,
    );
  }

  async preloadTemplate(designKey: CatalogueDesignKey): Promise<VisualTemplate | null> {
    try {
      return await this.instantiateTemplate(designKey);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Error al precargar VisualTemplate "${designKey}"`, error);
      }
      throw error;
    }
  }
}

export const templateRegistry = new TemplateRegistry();
